import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild, Input } from "@angular/core";
import { MatTableModule } from "@angular/material/table";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CoreService } from "../../../services/core.service";
import { MatIconModule } from "@angular/material/icon";
import { catchError } from "rxjs/operators";
import { of as observableOf } from "rxjs";
import { StoryArticleI } from "../../../interfaces/core.interface";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: "app-social-ai-story-table",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    ClipboardModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: "./social-ai-story-table.component.html",
  styleUrls: ["./social-ai-story-table.component.scss"],
})
export class SocialAiStoryTableComponent implements OnInit {
  @Input() companyID: number = 1;

  displayedColumns: string[] = [
    "ID",
    "Story Article Url",
    "Story",
    "Publication Platform",
    "created_at",
    "Published Story",
  ];

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  dataSource = new MatTableDataSource<StoryArticleI>([]);
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private coreService: CoreService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.loadStories();
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  loadStories(): void {
    const params = {
      companyID: this.companyID,
      page: this.currentPage,
    };

    this.coreService
      .getData("/getDataForStory", params)
      .pipe(
        catchError(() =>
          observableOf({ data: [], total: 0, per_page: 12, current_page: 1 })
        )
      )
      .subscribe((res: any) => {
        console.log("Fetched stories:", res);
        this.dataSource.data = res.data;
        this.totalItems = res.total;
        this.pageSize = res.per_page;
        this.currentPage = res.current_page;
      });
  }

  isVideoStory(tag: string): boolean {
    return ['fbstoryvideo', 'igstoryvideo', 'tiktokstoryvideo'].includes(tag);
  }

  isImageStory(tag: string): boolean {
    return ['fbstoryphoto', 'igstoryphoto', 'tiktokstoryphoto'].includes(tag);
  }

  onCopy(url: string) {
    this.coreService.showSnackbar("Copied!");
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadStories();
  }

  onPublishedStatusChange(element: StoryArticleI, isChecked: boolean): void {
    const requestBody = {
      storyId: element.posting_queue_id,
      isPublished: isChecked ? 1 : 0,
      platform: (element.platform || "").toLowerCase(),
    };

    this.coreService
      .postData("updateStoryPublishedStatus", requestBody)
      .subscribe({
        next: (response) => {
          if (response && response.success) {
            element.is_story_published = requestBody.isPublished;

            if (requestBody.isPublished === 1) {
              this.dataSource.data = this.dataSource.data.filter(
                (item) => item.posting_queue_id !== element.posting_queue_id
              );
              this.totalItems--;
            }
          } else {
            console.error("Backend reported failure:", response.message);
            element.is_story_published =
              element.is_story_published === 1 ? 0 : 1;
          }
        },
        error: (error) => {
          console.error("Error updating story:", error);
          element.is_story_published = element.is_story_published === 1 ? 0 : 1;
        },
      });
  }
  //Sidebar helpers 
  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      if (Array.isArray(parsed)) {
        this.posts = parsed.slice(0, this.maxSidebarCards);
      } else {
        this.posts = [];
      }
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
    }
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;

  trackByAlertId = (_: number, item: Alert) => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }
}
